import { CENTS_PER_KM_CAP_KM, CENTS_PER_KM_RATES, calculateCentsPerKm } from "@stb/jurisdiction-au"

import {
  ATO_BUSINESS_KM_URL,
  capKm,
  displayYear,
  HOME_FINANCIAL_YEAR,
  rate,
} from "../../components/home/home-content"
import { CentsPerKmCalculator, formatAud, SimpleTable, SourceLine } from "../../components/landing"
import { CentsScene } from "../../components/landing/product-art"
import { rateGuide } from "../cents-per-km-calculator"
import type { Guide } from "../guide-types"
import { englishDateToZh } from "./dates"

/**
 * Chinese counterpart of content/cents-per-km-calculator.tsx (T1320). The rates, the cap, the sources and the dates
 * come from the same constants as the English page; only the words differ. Terms: docs/design/landing/copy-zh.md.
 */
const fy = displayYear(HOME_FINANCIAL_YEAR)
const startYear = Number(HOME_FINANCIAL_YEAR.slice(0, 4))
const checked = englishDateToZh(rateGuide.checked)

const maxClaim = (year: string) => {
  const r = calculateCentsPerKm(year, CENTS_PER_KM_CAP_KM)
  return r.ok ? formatAud(r.deduction) : ""
}

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

/** Newest two years, as on the English page; older years live on the (English) history page. */
const recent = CENTS_PER_KM_RATES.toReversed().slice(0, 2)

const EXAMPLE_KM = 3200
const example = calculateCentsPerKm(HOME_FINANCIAL_YEAR, EXAMPLE_KM)

export const rateGuideZh: Guide = {
  slug: rateGuide.slug,
  title: "cents per km 费率与计算器",
  description: `ATO 的 cents per km 费率是每公里 ${rate.cents} 分（${fy} 财年），每辆车最多算 ${capKm} km。用免费工具估算你的扣除额。`,
  crumb: "cents per km 费率",
  h1: `ATO cents per km 费率 ${fy} 财年：每公里 ${rate.cents} 分`,
  published: rateGuide.published,
  reviewed: rateGuide.reviewed,
  badge: "免费工具",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO，cents per km 法（个人，英文）", href: rate.sourceUrl },
    { label: "业务页面（英文）", href: ATO_BUSINESS_KM_URL },
  ],
  answer: `${fy} 收入年度（${startYear}年7月1日至${startYear + 1}年6月30日），car 的 cents per km 费率是每公里 ${rate.cents} 分。每辆车最多可申报 ${capKm} 业务公里。这个费率已包含燃油、车辆登记费、保险、维修和车辆折旧。`,
  figure: example.ok ? (
    <figure className="bg-card border-border grid gap-2 rounded-xl border p-4">
      <CentsScene
        km={EXAMPLE_KM}
        capKm={CENTS_PER_KM_CAP_KM}
        rate={`${rate.cents} c`}
        year={`${fy} 财年`}
        result={formatAud(example.deduction)}
      />
      <figcaption className="text-muted-foreground text-center text-sm">
        例子：{fy} 财年，{EXAMPLE_KM.toLocaleString("en-AU")} 业务 km × {rate.cents} c/km，扣除额是{" "}
        {formatAud(example.deduction)}（上限 0 至 {capKm} km）。
      </figcaption>
    </figure>
  ) : undefined,
  related: [
    { label: "里程记录：自动 GPS 行车日志", href: "/mileage-tracker" },
    { label: "logbook 法：什么时候更划算", href: "/logbook-method" },
    {
      label: "ATO 对 logbook 的要求：要记录什么（英文）",
      href: "/guides/ato-logbook-requirements",
    },
  ],
  toc: [
    { id: "calculator", label: "算出你的扣除额" },
    { id: "rates", label: "历年费率" },
    { id: "which-method", label: "cents per km 还是 logbook？" },
    { id: "faq", label: "常见问题" },
  ],
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          算出你的扣除额
        </h2>
        <CentsPerKmCalculator />
      </section>

      <section aria-labelledby="rates" className="grid gap-3">
        <h2 id="rates" className="text-2xl font-semibold">
          历年费率
        </h2>
        <SimpleTable
          caption="历年费率"
          columns={["收入年度", "费率", `${capKm} km 的最高申报额`]}
          rows={[
            ...recent.map((r) => ({
              header: displayYear(r.financialYear),
              cells: [`${r.cents} c/km`, maxClaim(r.financialYear)],
            })),
            {
              header: "更早的年度",
              cells: [
                "见归档",
                <a key="history" href="/guides/ato-cents-per-km-rate-history" className={linkClass}>
                  费率历史（英文）
                </a>,
              ],
            },
          ]}
        />
        <SourceLine
          sources={[{ label: "ATO（英文）", href: rate.sourceUrl }]}
          financialYear={recent.map((r) => displayYear(r.financialYear)).join(" 和 ")}
          checked={checked}
        />
        <p className="text-muted-foreground text-sm">
          最高申报额是我们自己的算术（费率 × {capKm} km），不是 ATO 公布的数字。
        </p>
      </section>

      <section aria-labelledby="which-method" className="grid gap-3">
        <h2 id="which-method" className="text-2xl font-semibold">
          cents per km 还是 logbook？
        </h2>
        <p className="max-w-[70ch]">
          业务行驶的公里数不多、想简单申报时，用 cents per km 法。业务行驶多，或者车辆开支高时，用
          logbook 法。用 cents per km 法，每辆车最多申报 {capKm} km；业务行驶更多的话，logbook
          法可能扣除更多，但需要 12 周的行车日志和实际开支记录。
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "cents per km 法需要收据吗？",
      answer: `用这种方法，车辆开支不需要收据，但你必须能说明业务公里数是怎么算出来的。来源：ATO，cents per km 法，${fy} 财年，核对于 ${checked}。`,
    },
    {
      question: "费率由谁定？",
      answer:
        "cents per km 费率由 ATO 在立法文件中公布，不是每年都变。申报前请到 ATO 页面核对当前数字。",
    },
  ],
}
